import { ReactNode, useEffect, useState, useRef, createContext, useContext, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

interface RecaptchaContextValue {
  executeRecaptcha: ((action: string) => Promise<string>) | null;
  isReady: boolean;
}

const RecaptchaContext = createContext<RecaptchaContextValue>({
  executeRecaptcha: null,
  isReady: false,
});

export const useRecaptchaContext = () => useContext(RecaptchaContext);

interface RecaptchaProviderProps {
  children: ReactNode;
}

const RecaptchaProvider = ({ children }: RecaptchaProviderProps) => {
  const [isReady, setIsReady] = useState(false);
  const siteKeyRef = useRef<string | null>(null);
  const scriptRef = useRef<HTMLScriptElement | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("get-recaptcha-key");
        if (cancelled || error || !data?.siteKey) {
          if (error) console.error("Failed to fetch reCAPTCHA site key:", error);
          return;
        }
        
        siteKeyRef.current = data.siteKey;

        // Load reCAPTCHA script imperatively to avoid React DOM conflicts
        const script = document.createElement("script");
        script.src = `https://www.google.com/recaptcha/api.js?render=${data.siteKey}`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
          if (cancelled) return;
          const checkReady = () => {
            if (cancelled) return;
            if (window.grecaptcha && window.grecaptcha.execute) {
              window.grecaptcha.ready(() => {
                if (!cancelled) setIsReady(true);
              });
            } else {
              setTimeout(checkReady, 100);
            }
          };
          checkReady();
        };
        scriptRef.current = script;
        document.head.appendChild(script);
      } catch (err) {
        console.error("Error loading reCAPTCHA:", err);
      }
    })();

    return () => {
      cancelled = true;
      // Remove the script
      if (scriptRef.current && scriptRef.current.parentNode) {
        scriptRef.current.parentNode.removeChild(scriptRef.current);
      }
      // Remove all reCAPTCHA-injected elements
      document.querySelectorAll(".grecaptcha-badge").forEach(el => el.parentElement?.remove());
      // Remove any reCAPTCHA iframes
      document.querySelectorAll('iframe[src*="recaptcha"]').forEach(el => el.parentElement?.remove());
    };
  }, []);

  const executeRecaptcha = useCallback(async (action: string): Promise<string> => {
    if (!window.grecaptcha || !siteKeyRef.current) {
      throw new Error("reCAPTCHA not ready");
    }
    return window.grecaptcha.execute(siteKeyRef.current, { action });
  }, []);

  return (
    <RecaptchaContext.Provider value={{ executeRecaptcha: isReady ? executeRecaptcha : null, isReady }}>
      {children}
    </RecaptchaContext.Provider>
  );
};

// Augment window type for grecaptcha
declare global {
  interface Window {
    grecaptcha: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

export default RecaptchaProvider;
